/**
 * Life4Earth V2 — Admin Role Catalogue · Bundle Create/Edit tray.
 *
 * DEV-1214 iter 2. Slide-in form for creating or editing a Role Bundle.
 * Modeled after AddRoleTray and the SlotEditModal tray pattern.
 *
 * Modes:
 *   - "create" — bundle_key prefilled with "bundle_" prefix, user fills suffix.
 *   - "edit"   — bundle_key locked (server-enforced immutable post-create).
 *
 * Fields:
 *   - bundleKey (required, must start with bundle_; immutable on edit)
 *   - label (required)
 *   - description (optional)
 *   - applicableEntityTypes (multi-select; suggestion-driven chip input)
 *   - applicableProjectCategories (conditional — only when applicableEntityTypes
 *       contains full_project; chip input, must match Project Taxonomy labels)
 *   - audienceEntityGuids (search-driven chip picker over community_entities)
 *
 * Sysadmin-only — server gates via RequireSysadmin in RoleBundlesController.
 * English-only per CLAUDE.md (admin surface).
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  createRoleBundle,
  updateRoleBundle,
  type CreateRoleBundleRequest,
  type RoleBundleResponse,
  type UpdateRoleBundleRequest,
} from "../../api/roleBundles";
import {
  fetchAllCommunityEntities,
  type CommunityEntityResponse,
} from "../../api/communityMetadata";
import {
  ENTITY_TYPE_CATALOG,
  ENTITY_TYPE_PILLAR_LABEL,
  ENTITY_TYPE_PILLAR_ORDER,
  getEntityTypeMeta,
  type EntityTypeMeta,
} from "../../types/entityTypes";
import styles from "./AddRoleTray.module.css";

interface Props {
  mode: "create" | "edit";
  /** Required for edit mode; ignored for create. */
  initialBundle?: RoleBundleResponse;
  open: boolean;
  onClose: () => void;
  /** Fires after a successful save with the saved bundle's GUID. */
  onSaved: (savedGuid: string) => void;
}

export default function BundleEditTray({
  mode,
  initialBundle,
  open,
  onClose,
  onSaved,
}: Props) {
  const isEdit = mode === "edit";

  // ── Form state ──────────────────────────────────────────────

  const [bundleKey, setBundleKey] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [applicableEntityTypes, setApplicableEntityTypes] = useState<string[]>([]);
  const [applicableProjectCategories, setApplicableProjectCategories] = useState<string[]>([]);
  const [audienceEntityGuids, setAudienceEntityGuids] = useState<string[]>([]);

  // Per-input drafts (chip patterns need a buffer for what's being typed
  // before commit on Enter/comma).
  const [entityTypeDraft, setEntityTypeDraft] = useState("");
  const [projectCategoryDraft, setProjectCategoryDraft] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Reset / seed on open ────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    if (isEdit && initialBundle) {
      setBundleKey(initialBundle.bundleKey);
      setLabel(initialBundle.label);
      setDescription(initialBundle.description ?? "");
      setApplicableEntityTypes([...initialBundle.applicableEntityTypes]);
      setApplicableProjectCategories([...initialBundle.applicableProjectCategories]);
      setAudienceEntityGuids([...initialBundle.audienceEntityGuids]);
    } else {
      // Create mode — prefill key with prefix so the sysadmin doesn't have
      // to remember the bundle_ convention (CHECK constraint enforces it).
      setBundleKey("bundle_");
      setLabel("");
      setDescription("");
      setApplicableEntityTypes([]);
      setApplicableProjectCategories([]);
      setAudienceEntityGuids([]);
    }
    setEntityTypeDraft("");
    setProjectCategoryDraft("");
    setError(null);
  }, [open, isEdit, initialBundle]);

  // ── Audience picker — fetch entities once on first open ─────

  const [allEntities, setAllEntities] = useState<CommunityEntityResponse[]>([]);
  const [entitySearch, setEntitySearch] = useState("");
  const [loadingEntities, setLoadingEntities] = useState(false);

  useEffect(() => {
    if (!open || allEntities.length > 0) return;
    setLoadingEntities(true);
    fetchAllCommunityEntities()
      .then((list) => setAllEntities(list))
      .catch(() => {})
      .finally(() => setLoadingEntities(false));
  }, [open, allEntities.length]);

  const entityByGuid = useMemo(() => {
    const map = new Map<string, CommunityEntityResponse>();
    for (const e of allEntities) map.set(e.entityGuid.toUpperCase(), e);
    return map;
  }, [allEntities]);

  const entitySearchResults = useMemo<CommunityEntityResponse[]>(() => {
    const q = entitySearch.trim().toLowerCase();
    if (q.length < 2) return [];
    const selected = new Set(audienceEntityGuids.map((g) => g.toUpperCase()));
    return allEntities
      .filter((e) => !selected.has(e.entityGuid.toUpperCase()))
      .filter((e) =>
        e.name.toLowerCase().includes(q)
        || e.entityType.toLowerCase().includes(q),
      )
      .slice(0, 10); // cap dropdown to avoid wall-of-rows
  }, [allEntities, entitySearch, audienceEntityGuids]);

  // ── Derived flags ───────────────────────────────────────────

  const showProjectCategories = applicableEntityTypes.includes("full_project");
  const labelOk = label.trim().length > 0;
  const keyOk = isEdit || (
    bundleKey.trim().startsWith("bundle_")
    && bundleKey.trim().length > "bundle_".length
  );
  const canSave = labelOk && keyOk && !saving;

  // ── Chip helpers ────────────────────────────────────────────

  const addEntityType = (raw: string) => {
    const value = raw.trim();
    if (!value) return;
    if (applicableEntityTypes.includes(value)) return;
    setApplicableEntityTypes([...applicableEntityTypes, value]);
    setEntityTypeDraft("");
  };

  const removeEntityType = (value: string) => {
    setApplicableEntityTypes(applicableEntityTypes.filter((t) => t !== value));
    // If we just dropped full_project, also clear project categories (the
    // section is hidden anyway; clearing prevents stale data persisting
    // through a save).
    if (value === "full_project") setApplicableProjectCategories([]);
  };

  const addProjectCategory = (raw: string) => {
    const value = raw.trim();
    if (!value) return;
    if (applicableProjectCategories.includes(value)) return;
    setApplicableProjectCategories([...applicableProjectCategories, value]);
    setProjectCategoryDraft("");
  };

  const removeProjectCategory = (value: string) => {
    setApplicableProjectCategories(
      applicableProjectCategories.filter((c) => c !== value),
    );
  };

  const addAudienceEntity = (guid: string) => {
    if (audienceEntityGuids.some((g) => g.toUpperCase() === guid.toUpperCase())) return;
    setAudienceEntityGuids([...audienceEntityGuids, guid]);
    setEntitySearch("");
  };

  const removeAudienceEntity = (guid: string) => {
    setAudienceEntityGuids(
      audienceEntityGuids.filter((g) => g.toUpperCase() !== guid.toUpperCase()),
    );
  };

  // ── Save ────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      let savedGuid: string;
      if (isEdit && initialBundle) {
        const req: UpdateRoleBundleRequest = {
          label: label.trim(),
          description: description.trim() || null,
          applicableEntityTypes,
          applicableProjectCategories: showProjectCategories
            ? applicableProjectCategories
            : [],
          audienceEntityGuids,
        };
        const saved = await updateRoleBundle(initialBundle.roleBundleGuid, req);
        savedGuid = saved.roleBundleGuid;
      } else {
        const req: CreateRoleBundleRequest = {
          bundleKey: bundleKey.trim(),
          label: label.trim(),
          description: description.trim() || null,
          applicableEntityTypes,
          applicableProjectCategories: showProjectCategories
            ? applicableProjectCategories
            : [],
          audienceEntityGuids,
        };
        const created = await createRoleBundle(req);
        savedGuid = created.roleBundleGuid;
      }
      onSaved(savedGuid);
      onClose();
    } catch (err: any) {
      setError(err?.message || "Failed to save Role Bundle.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // ── Render ──────────────────────────────────────────────────

  return (
    <>
      <div
        className={styles.backdrop}
        onClick={() => { if (!saving) onClose(); }}
        aria-hidden="true"
      />
      <aside
        className={styles.tray}
        role="dialog"
        aria-labelledby="bundle-edit-title"
        style={{ maxWidth: 560 }}
      >
        <header className={styles.header}>
          <div>
            <div className={styles.eyebrow}>Role Bundle</div>
            <h2 id="bundle-edit-title" className={styles.title}>
              {isEdit ? "Edit bundle" : "Create bundle"}
            </h2>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.body}>
          {/* Bundle key */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              Bundle key {isEdit && <em style={{ fontWeight: 400, color: "var(--text-muted)" }}>(locked after create)</em>}
            </span>
            <input
              type="text"
              className={styles.textInput}
              value={bundleKey}
              onChange={(e) => setBundleKey(e.target.value)}
              placeholder="bundle_org_rotary_club_standard"
              maxLength={100}
              disabled={isEdit || saving}
              style={{ fontFamily: "monospace" }}
            />
            <span className={styles.fieldHint}>
              Globally unique identifier. Must start with <code>bundle_</code>.
              Use snake_case (e.g., <code>bundle_org_civic_club_standard</code>).
              Not editable after create.
            </span>
          </label>

          {/* Label */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Label</span>
            <input
              type="text"
              className={styles.textInput}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Rotary Club Standard"
              maxLength={200}
              autoFocus
            />
            <span className={styles.fieldHint}>
              Display name shown in pickers and detail panes.
            </span>
          </label>

          {/* Description */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Description (optional)</span>
            <textarea
              className={styles.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What operational pattern does this bundle represent? Who is it for?"
              rows={3}
            />
          </label>

          {/* Applicable entity types — DEV-1214 iter 2.5 grouped picker.
              Suggestions grouped by pillar with friendly labels; chips show
              label + technical key as subtitle. Free-form input retained
              for new types not yet in ENTITY_TYPE_CATALOG (DEV-1248 will
              move the catalog to a DB-backed admin-managed table). */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Applicable entity types</span>
            <span className={styles.fieldHint}>
              Communities can only apply this bundle if their entity type is in
              this list. Leave empty to allow any entity type.
            </span>

            <EntityTypeChipRow
              selected={applicableEntityTypes}
              onRemove={removeEntityType}
            />

            <EntityTypeSuggestionPicker
              selected={applicableEntityTypes}
              onAdd={addEntityType}
            />

            <details style={{ marginBlockStart: 8, fontSize: 12 }}>
              <summary style={{ cursor: "pointer", color: "var(--text-muted)" }}>
                Add a custom entity type (advanced)
              </summary>
              <div style={{ marginBlockStart: 6 }}>
                <span className={styles.fieldHint}>
                  Type a raw <code>entity_type</code> value (e.g.,{" "}
                  <code>org_lions_club</code>) and press Enter. Must match an
                  existing or planned <code>community_entities.entity_type</code>
                  value. Per migration 213, must start with <code>org_</code> or{" "}
                  <code>geo_</code> (or be <code>organization</code> /{" "}
                  <code>solo_creator_workshop</code>).
                </span>
                <input
                  type="text"
                  className={styles.textInput}
                  value={entityTypeDraft}
                  onChange={(e) => setEntityTypeDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addEntityType(entityTypeDraft);
                    }
                  }}
                  placeholder="org_lions_club"
                  style={{ fontFamily: "monospace", marginBlockStart: 6 }}
                />
              </div>
            </details>
          </div>

          {/* Applicable project categories — conditional render per
              locked micro-decision B (only when full_project is in
              entity types). */}
          {showProjectCategories && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Applicable project categories</span>
              <span className={styles.fieldHint}>
                Restricts this bundle to projects matching the listed
                categories. Leave empty to apply to all project categories.
                Values must match labels from your Project Taxonomy
                (category level).
              </span>

              <ChipRow
                items={applicableProjectCategories}
                onRemove={removeProjectCategory}
              />

              <input
                type="text"
                className={styles.textInput}
                value={projectCategoryDraft}
                onChange={(e) => setProjectCategoryDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addProjectCategory(projectCategoryDraft);
                  }
                }}
                placeholder="Type a category label and press Enter"
                style={{ marginBlockStart: 6 }}
              />
            </div>
          )}

          {/* Audience entity picker */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Audience (optional)</span>
            <span className={styles.fieldHint}>
              Restrict visibility to specific entities and their descendants.
              Empty = platform-wide. Add a Rotary International parent here
              to make a bundle visible only to Rotary clubs in its hierarchy.
            </span>

            {audienceEntityGuids.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBlockStart: 6 }}>
                {audienceEntityGuids.map((guid) => {
                  const e = entityByGuid.get(guid.toUpperCase());
                  return (
                    <Chip
                      key={guid}
                      onRemove={() => removeAudienceEntity(guid)}
                    >
                      {e ? e.name : <span style={{ fontFamily: "monospace" }}>{guid}</span>}
                      {e && (
                        <span style={{ color: "var(--text-muted)", marginInlineStart: 6, fontSize: 11 }}>
                          {e.entityType}
                        </span>
                      )}
                    </Chip>
                  );
                })}
              </div>
            )}

            <input
              type="text"
              className={styles.textInput}
              value={entitySearch}
              onChange={(e) => setEntitySearch(e.target.value)}
              placeholder={loadingEntities ? "Loading entities…" : "Search entities by name"}
              style={{ marginBlockStart: 6 }}
              disabled={loadingEntities}
            />

            {entitySearchResults.length > 0 && (
              <div
                style={{
                  border: "1px solid var(--border-default)",
                  borderRadius: 4,
                  marginBlockStart: 4,
                  maxHeight: 200,
                  overflowY: "auto",
                  background: "var(--bg-surface)",
                }}
              >
                {entitySearchResults.map((e) => (
                  <button
                    key={e.entityGuid}
                    type="button"
                    onClick={() => addAudienceEntity(e.entityGuid)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "start",
                      padding: "6px 10px",
                      background: "transparent",
                      border: "none",
                      borderBottom: "1px solid var(--border-default)",
                      cursor: "pointer",
                      color: "var(--text-primary)",
                      fontSize: 13,
                    }}
                  >
                    {e.name}
                    <span style={{ color: "var(--text-muted)", marginInlineStart: 8, fontSize: 11, fontFamily: "monospace" }}>
                      {e.entityType}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {entitySearch.trim().length >= 2 && entitySearchResults.length === 0 && !loadingEntities && (
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBlockStart: 4, fontStyle: "italic" }}>
                No matching entities.
              </div>
            )}
          </div>
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving
              ? (isEdit ? "Saving…" : "Creating…")
              : (isEdit ? "Save changes" : "Create bundle")}
          </button>
        </footer>
      </aside>
    </>
  );
}

// ────────────────────────────────────────────────────────────
// Small inline UI primitives
// ────────────────────────────────────────────────────────────

function Chip({
  children,
  onRemove,
}: {
  children: React.ReactNode;
  onRemove: () => void;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 4px 2px 8px",
        background: "var(--bg-elevated)",
        color: "var(--text-primary)",
        borderRadius: 12,
        fontSize: 12,
      }}
    >
      {children}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove"
        style={{
          background: "transparent",
          border: "none",
          color: "var(--text-muted)",
          cursor: "pointer",
          fontSize: 14,
          lineHeight: 1,
          padding: "0 2px",
        }}
      >
        ×
      </button>
    </span>
  );
}

function ChipRow({
  items,
  onRemove,
  monospace,
}: {
  items: string[];
  onRemove: (v: string) => void;
  monospace?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBlockStart: 6 }}>
      {items.map((v) => (
        <Chip key={v} onRemove={() => onRemove(v)}>
          <span style={{ fontFamily: monospace ? "monospace" : undefined }}>
            {v}
          </span>
        </Chip>
      ))}
    </div>
  );
}

/**
 * DEV-1214 iter 2.5. Chip row specialized for entity_types — renders the
 * friendly label as primary text with the technical key as a small muted
 * monospace subtitle. Falls back to the raw key for free-form custom values
 * not in ENTITY_TYPE_CATALOG.
 */
function EntityTypeChipRow({
  selected,
  onRemove,
}: {
  selected: string[];
  onRemove: (key: string) => void;
}) {
  if (selected.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBlockStart: 6 }}>
      {selected.map((key) => {
        const meta = getEntityTypeMeta(key);
        return (
          <Chip key={key} onRemove={() => onRemove(key)}>
            {meta ? (
              <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
                <span>{meta.label}</span>
                <span style={{ color: "var(--text-muted)", fontSize: 10, fontFamily: "monospace" }}>
                  {meta.key}
                </span>
              </span>
            ) : (
              <span style={{ fontFamily: "monospace" }}>{key}</span>
            )}
          </Chip>
        );
      })}
    </div>
  );
}

/**
 * DEV-1214 iter 2.5. Suggestion picker for entity types — clickable chips
 * grouped by pillar. Already-selected entries are filtered out. Clicking
 * a chip adds it to the bundle's applicable_entity_types.
 */
function EntityTypeSuggestionPicker({
  selected,
  onAdd,
}: {
  selected: string[];
  onAdd: (key: string) => void;
}) {
  const selectedSet = new Set(selected);
  // Group catalog by pillar
  const byPillar = new Map<EntityTypeMeta['pillar'], EntityTypeMeta[]>();
  for (const meta of ENTITY_TYPE_CATALOG) {
    if (selectedSet.has(meta.key)) continue;
    const list = byPillar.get(meta.pillar) ?? [];
    list.push(meta);
    byPillar.set(meta.pillar, list);
  }

  // Nothing left to suggest (all in the catalog have been selected).
  if (byPillar.size === 0) {
    return (
      <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginBlockStart: 8 }}>
        All catalog entity types are selected.
      </div>
    );
  }

  return (
    <div style={{ marginBlockStart: 8, display: "flex", flexDirection: "column", gap: 8 }}>
      {ENTITY_TYPE_PILLAR_ORDER.map((pillar) => {
        const items = byPillar.get(pillar);
        if (!items || items.length === 0) return null;
        return (
          <div key={pillar}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--text-secondary)",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                marginBlockEnd: 4,
              }}
            >
              {ENTITY_TYPE_PILLAR_LABEL[pillar]}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {items.map((meta) => (
                <button
                  key={meta.key}
                  type="button"
                  onClick={() => onAdd(meta.key)}
                  title={`${meta.description} · ${meta.key}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "baseline",
                    gap: 6,
                    padding: "3px 8px",
                    background: "transparent",
                    color: "var(--text-primary)",
                    border: "1px dashed var(--border-default)",
                    borderRadius: 12,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  <span>+ {meta.label}</span>
                  <span style={{ color: "var(--text-muted)", fontSize: 10, fontFamily: "monospace" }}>
                    {meta.key}
                  </span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
